import { Divider, Form, FormInstance, Input, Row, Select } from "antd";
import ResponsiveCol from "../../../../common/components/form/ResponsiveCol";
import Validations from "./DistributionPointValidations";
import tooltipList from "../tooltip.json";
import { regulatoryCharacter } from "services/utils/StaticInfoEnums";
import {
  DistributionPointRequestResource,
  RequestRegulatoryPermissionKeys,
} from "api/generated";
import { useEffect, useMemo } from "react";
import { FormItem } from "common/components/form/FormItem";

interface RegulatoryDataFormProps {
  formData: FormInstance<DistributionPointRequestResource>;
  regulatoryPermissionKeys: RequestRegulatoryPermissionKeys[];
}

function RegulatoryDataForm({
  formData,
  regulatoryPermissionKeys,
}: RegulatoryDataFormProps) {
  const character = Form.useWatch("character", formData);
  const senerLicense = Form.useWatch("senerLicense", formData);
  const permissionModality = Form.useWatch("permissionModality", formData);
  const isPermissionary = useMemo(() => {
    return character === "PERMISIONARIO";
  }, [character]);

  useEffect(() => {
    if (isPermissionary) {
      formData.setFieldValue("permissionModality", undefined);
      formData.setFieldValue("regulatoryCharacterField1", senerLicense);
    } else {
      formData.setFieldValue("permissionModality", senerLicense);
    }
  }, [isPermissionary, senerLicense]);

  const showContractAssignNumber = useMemo(() => {
    switch (character) {
      case "PERMISIONARIO":
      case "USUARIO":
      case undefined:
        return false;
      default:
        return true;
    }
  }, [character]);

  return (
    <div>
      <Divider
        orientation="left"
        style={{ fontSize: "15px", color: "#606676" }}
      >
        Caracter regulatorio
      </Divider>
      <Row gutter={24}>
        <ResponsiveCol size="lg">
          <FormItem<DistributionPointRequestResource>
            label="Caracter regulatorio"
            name="character"
            rules={Validations.CharacterRules}
          >
            <Select
              placeholder="Selecciona el carácter con el que actúa para efectos regulatorios"
              showSearch
              filterOption={Validations.FilterSelectOpts}
              suffixIcon={Validations.ExtraInfoBtn(
                "Selecciona el carácter con el que actúa para efectos regulatorios",
                [tooltipList.descripcion]
              )}
            >
              {regulatoryCharacter.map((item) => {
                return (
                  <Select.Option key={item.value} value={item.value}>
                    {item.text}
                  </Select.Option>
                );
              })}
            </Select>
          </FormItem>
        </ResponsiveCol>
        {!isPermissionary && (
          <ResponsiveCol size="lg">
            <FormItem<DistributionPointRequestResource>
              label="Número de permiso regulatorio"
              name="regulatoryCharacterField1"
            >
              <Input
                className="common-input"
                placeholder="Introduce el número de permiso regulatorio"
                disabled={true}
                addonAfter={Validations.ExtraInfoBtn(
                  "Introduce el número de permiso regulatorio",
                  [tooltipList.caracter.permisionario]
                )}
              />
            </FormItem>
          </ResponsiveCol>
        )}

        {showContractAssignNumber && (
          <ResponsiveCol size="lg">
            <FormItem<DistributionPointRequestResource>
              label="Número de contrato o asignación"
              name="regulatoryCharacterField1"
              rules={Validations.ContractAsignationNumberRules}
            >
              <Input
                className="common-input"
                placeholder="Número de contrato o asignación"
                addonAfter={Validations.ExtraInfoBtn(
                  "Introduce el número de contrato o asignación",
                  [tooltipList.caracter.contratistaAsigantario]
                )}
              />
            </FormItem>
          </ResponsiveCol>
        )}

        {isPermissionary && (
          <ResponsiveCol size="lg">
            <FormItem<DistributionPointRequestResource>
              label="Modalidad de permiso"
              name="permissionModality"
              rules={Validations.PermissionModalityRules}
            >
              <Select
                placeholder="Modalidad de permiso"
                showSearch
                filterOption={Validations.FilterSelectOpts}
                suffixIcon={Validations.ExtraInfoBtn(
                  "Selecciona la modalidad de permiso",
                  [tooltipList.caracter.modalidadPermiso]
                )}
                prefix={
                  isPermissionary && permissionModality
                    ? permissionModality
                    : undefined
                }
              >
                {regulatoryPermissionKeys.map((item) => {
                  return (
                    <Select.Option
                      key={item.regulatoryPermissionCode}
                      value={item.regulatoryPermissionCode}
                    >
                      {item.regulatoryPermissionDescription}
                    </Select.Option>
                  );
                })}
              </Select>
            </FormItem>
          </ResponsiveCol>
        )}
        {character === "USUARIO" && (
          <ResponsiveCol size="lg">
            <FormItem<DistributionPointRequestResource>
              label="Descripción de instalación"
              name="regulatoryCharacterField1"
              rules={Validations.NaturalGasStorageInstalationRules}
              extra={tooltipList.caracter.descripcionInstalacion}
            >
              <Input.TextArea
                rows={2}
                placeholder="Introduce la descripción de la instalación"
              />
            </FormItem>
          </ResponsiveCol>
        )}
        {/* Debido a las combinatorias por información regulatoria, 
        los siguientes FormItem estarán presentes pero ocultos,
        con ello nos aseguraremos de incluirlos payload
         */}
        <FormItem<DistributionPointRequestResource>
          hidden
          label="regulatoryCharacterField1"
          name="regulatoryCharacterField1"
        />
        <FormItem<DistributionPointRequestResource>
          hidden
          label="permissionModality"
          name="permissionModality"
        />
      </Row>
    </div>
  );
}

export default RegulatoryDataForm;
